import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import LocationPicker from "@/components/LocationPicker";
import { MapPin, Plus, Trash2, Users, Crown } from "lucide-react";

export default function ZonesPanel() {
  const [zones, setZones] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", center_lat: 40.7128, center_lng: -74.006, radius_miles: 9.0, active: true });
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/zones");
      setZones(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const submit = async () => {
    setErr("");
    try {
      await api.post("/zones", form);
      setCreating(false);
      setForm({ name: "", center_lat: 40.7128, center_lng: -74.006, radius_miles: 9.0, active: true });
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeZone = async (id) => {
    if (!window.confirm("Delete this zone? All drivers in it will be unassigned.")) return;
    try {
      await api.delete(`/zones/${id}`);
      load();
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="zones-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-asap-orange font-bold">Service Zones</div>
          <h2 className="font-display text-2xl uppercase text-asap-deep">Regional Rollout</h2>
          <p className="text-sm text-slate-500 mt-1">First 7 drivers per zone are founding (pay-per-job, free). Drivers #8+ require a $29/month subscription.</p>
        </div>
        <button data-testid="new-zone-btn" onClick={() => setCreating((c) => !c)} className="inline-flex items-center gap-2 px-4 py-2 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">
          <Plus size={16}/> New Zone
        </button>
      </div>

      {creating && (
        <div className="bg-white border-2 border-asap-deep p-5 shadow-hard space-y-4" data-testid="zone-form">
          <input data-testid="zone-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Zone name (e.g., Salinas, CA)" className="w-full px-4 py-2 border-2 border-asap-deep outline-none font-bold"/>
          <div className="text-xs text-slate-500 -mt-2">Tap on the map to set the center</div>
          <LocationPicker value={{ lat: form.center_lat, lng: form.center_lng }} onChange={(p) => setForm({ ...form, center_lat: p.lat, center_lng: p.lng })} height={280}/>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep">Radius (miles)</div>
              <input data-testid="zone-radius" type="number" step="0.5" min="1" max="50" value={form.radius_miles} onChange={(e) => setForm({ ...form, radius_miles: parseFloat(e.target.value) })} className="w-full px-3 py-2 border-2 border-asap-deep outline-none"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep">Center</div>
              <div className="px-3 py-2 border-2 border-asap-deep bg-slate-50 text-xs font-mono">{form.center_lat.toFixed(4)}, {form.center_lng.toFixed(4)}</div>
            </label>
          </div>
          {err && <div className="text-asap-red font-bold text-sm">{err}</div>}
          <div className="flex gap-2">
            <button data-testid="zone-save" onClick={submit} className="px-5 py-2 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep">Save</button>
            <button onClick={() => setCreating(false)} className="px-5 py-2 border-2 border-slate-300">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="zones-list">
        {zones.length === 0 && <div className="text-slate-400 text-sm">No zones yet — create your first rollout region above.</div>}
        {zones.map((z) => (
          <div key={z.id} className="bg-white border-2 border-asap-deep p-5" data-testid={`zone-${z.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display text-xl uppercase text-asap-deep flex items-center gap-2"><MapPin size={18} className="text-asap-orange"/> {z.name}</div>
                <div className="text-xs text-slate-500 mt-1">{z.center_lat.toFixed(4)}, {z.center_lng.toFixed(4)} · {z.radius_miles}mi radius</div>
              </div>
              <button onClick={() => removeZone(z.id)} className="p-2 border-2 border-asap-red text-asap-red hover:bg-asap-red hover:text-white" data-testid={`zone-delete-${z.id}`}><Trash2 size={14}/></button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="border-2 border-asap-yellow bg-asap-yellow/10 p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-asap-orange flex items-center gap-1"><Crown size={10}/> Founding</div>
                <div className="font-display text-2xl text-asap-deep mt-1">{z.founding_count}/{z.founding_limit}</div>
              </div>
              <div className="border-2 border-asap-deep p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep flex items-center gap-1"><Users size={10}/> Slots Open</div>
                <div className="font-display text-2xl text-asap-deep mt-1">{z.founding_slots_open}</div>
              </div>
            </div>
            <div className="mt-3 h-2 bg-slate-200 rounded">
              <div className="h-full bg-asap-yellow rounded transition-all" style={{ width: `${(z.founding_count / z.founding_limit) * 100}%` }}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
